import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button, Stack, TextField, Typography } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import ControlWithLabel from 'src/components/ControlWithLabel';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useDeletePersonAccountMutation } from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/account/delete')({
    component: DeleteAccountComponent,
});

function DeleteAccountComponent() {
    const navigate = useNavigate();
    const { showError, showSuccess } = useSnackbar();
    const { currentUser: teamMember } = useTeamMemberData();

    const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

    const [deletePersonAccount, { isLoading: isDeleting }] = useDeletePersonAccountMutation();

    const { handleSubmit, control, formState } = useForm<{
        note: string;
    }>({ mode: 'onChange', defaultValues: { note: '' } });

    const onSubmit = async (data: { note: string }) => {
        if (!teamMember) return;

        try {
            await deletePersonAccount({
                personRid: teamMember.personRid,
                personDeleteAccountRequest: { deleteAccountReason: data.note },
            }).unwrap();

            showSuccess('Account deleted');
            navigate({ to: '/login' });
        } catch (error) {
            console.error('Failed to delete account:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleCancel = () => {
        history.back();
    };

    const handleConfirmDelete = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsDeleteConfirmed(event.target.checked);
    };

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle="Delete Account" />
            <Stack sx={{ gap: 4 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                        Sorry to see you go. Please tell us why you are deleting your account.
                    </Typography>
                    <Typography variant="body1">
                        Your feedback helps us improve the experience for others.
                    </Typography>
                </Stack>
                <Controller
                    name="note"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            multiline
                            rows={4}
                            inputRef={ref}
                            fullWidth
                            label="Add Notes (optional)"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />

                <ControlWithLabel
                    label="I understand that deleting my account will permanently remove all my data and access to Sage."
                    checked={isDeleteConfirmed}
                    onChange={handleConfirmDelete}
                />
            </Stack>
            <PageFooter>
                <Stack sx={{ gap: 2.5 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        color="errorInverse"
                        onClick={handleSubmit(onSubmit)}
                        disabled={!formState.isValid || !isDeleteConfirmed || isDeleting}
                        loading={isDeleting}
                    >
                        {isDeleting ? 'Deleting Account...' : 'Delete Account'}
                    </Button>
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={handleCancel}
                        disabled={!formState.isValid}
                    >
                        Cancel
                    </Button>
                </Stack>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
